import React, { createContext, useState, ReactNode, useMemo, useEffect, useCallback } from 'react'
import { User } from '../api/signin'
import { useStorage } from '../customHooks/useStorage'

type AuthContextType = {
  isAuthenticated: boolean
  user: User | null
  logout: () => void
  setUser: (userData: User | null) => void
  setIsAuthenticated: (isTrue: boolean) => void
  logoutInProgress: boolean
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const { value: userOnSession, remove: removeUserSession } = useStorage<User | null>(
    'user',
    null,
    'session'
  )
  const { remove: removeSelectedPlatform } = useStorage('platform', { id: '', name: '' }, 'session')

  const { remove: removeLinkAccBlock } = useStorage('link_acc_block_until', null, 'local')

  const [user, setUser] = useState<User | null>(() => {
    const stored = userOnSession
    return stored
  })

  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => {
    return !!userOnSession
  })

  const [logoutInProgress, setLogoutInProgress] = useState<boolean>(false)

  const logout = useCallback(() => {
    setLogoutInProgress(true)
    setUser(null)
    setIsAuthenticated(false)
    removeUserSession()
    removeLinkAccBlock()
    removeSelectedPlatform()
  }, [removeUserSession])

  // Set user on sessionStorage
  useEffect(() => {
    const validateUserSession = () => {
      if (!userOnSession) logout()
    }

    window.addEventListener('storage', validateUserSession)

    return () => window.removeEventListener('storage', validateUserSession)
  }, [logout])

  // Memoize the context value
  const value = useMemo(
    () => ({ isAuthenticated, user, setUser, logout, setIsAuthenticated, logoutInProgress }),
    [isAuthenticated, user, logout, logoutInProgress]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
